import { Button, Grid, Layout, Paper, Table, Alert, ButtonGroup, Loader, Accordion, AccordionDetails, AccordionSummary, Typography, Modal } from "../../components";
import { AIRLINES_OPTIONS, COMPANY_OPTIONS, COMPANY_OPTIONS_SORTED } from "../../constants";
import currency from 'currency.js'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import { useAxios, useGridUrlState, useToggle } from '../../hooks'
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import AuthContext from "../../context/auth";
import { useOrderBoardguardUpdates } from "../../context/notifications";
import { Box, Chip, MenuItem, Tooltip, ToggleButton, ToggleButtonGroup, Menu as MuiMenu } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { composeSummary, formatDate, formatDateTime, formatMoney, getOrderConfirmationColor, getOrderConfirmationLabel, getPaymentStatusColor, getPaymentStatusLabel } from "../../utils";
import { enqueueSnackbar } from 'notistack'
import type { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { QrCodePix } from "qrcode-pix";
import { Receipt } from "@mui/icons-material";
import { AccountModalBids, AccountModalBidsProps } from "../bids/AccountModal";
import {
    BOARDGUARD_PDF_LANG_OPTIONS,
    BOARDGUARD_STATUS_OPTIONS,
    BoardguardDisplayStatus,
    BoardguardPdfLang,
    BoardguardTicket,
    downloadTicketPdf,
    getBoardguardDisplayStatus,
    getBoardguardStatusColor,
    getBoardguardStatusLabel,
    getBoardguardTooltip,
    getNoShowTooltip,
    monitorOrderOnBoardguard,
    NoShowFields,
    pickNoShowFields,
    setOrderNoShow,
    triggerBoardguardCheck,
} from "../../utils/boardguard";

const BOARDGUARD_MENU_LABELS = {
    monitor: 'Monitorar no Boardguard',
    check: 'Verificar agora',
    pdfPt: 'Baixar PDF (Português)',
    pdfEn: 'Baixar PDF (English)',
};

const NO_SHOW_MENU_LABELS = ['Marcar como no-show', 'Desmarcar no-show'];

function StackCell({ primary, secondary }: { primary: ReactNode; secondary?: ReactNode }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, lineHeight: 1.25, py: 0.25, whiteSpace: 'normal' }}>
            <Typography variant="body2" component="div" sx={{ fontWeight: secondary ? 500 : 400 }}>
                {primary}
            </Typography>
            {secondary != null && secondary !== '' && (
                <Typography variant="caption" color="text.secondary" component="div">
                    {secondary}
                </Typography>
            )}
        </Box>
    );
}

const statusChipSx = {
    height: 'auto',
    maxWidth: '100%',
    alignSelf: 'flex-start',
    '& .MuiChip-label': {
        whiteSpace: 'normal',
        display: 'block',
        py: 0.25,
        lineHeight: 1.25,
    },
} as const;

/** Labels curtos só na grade — filtros/export mantêm o texto completo. */
function getCompactPaymentStatusLabel(status: string) {
    if (status === 'AWAITING_ORDER_CONFIRMATION') return 'Aguardando'
    return getPaymentStatusLabel(status) || status || '—'
}

function getBoardguardStatusIcon(status: BoardguardDisplayStatus) {
    switch (status) {
        case 'OK':
            return CheckCircleOutlineIcon
        case 'PENDING':
            return SyncOutlinedIcon
        case 'ATTENTION':
            return WarningAmberOutlinedIcon
        case 'CANCELLED':
        case 'ERROR':
            return ErrorOutlineIcon
        case 'ARCHIVED':
            return Inventory2OutlinedIcon
        case 'NOT_MONITORED':
        default:
            return VisibilityOffOutlinedIcon
    }
}

function MonitorIcons({
    status,
    ticket,
    noShow,
    noShowTitle,
}: {
    status: BoardguardDisplayStatus
    ticket?: BoardguardTicket | null
    noShow?: boolean
    noShowTitle?: string
}) {
    const StatusIcon = getBoardguardStatusIcon(status)
    const color = getBoardguardStatusColor(status)
    const statusLabel = getBoardguardStatusLabel(status)
    const detail = getBoardguardTooltip(ticket)
    const statusTitle = detail ? `${statusLabel} — ${detail}` : statusLabel

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, py: 0.25 }}>
            <Tooltip title={statusTitle}>
                <Box component="span" sx={{ display: 'inline-flex', lineHeight: 0 }}>
                    <StatusIcon
                        fontSize="small"
                        color={color === 'default' ? 'action' : color}
                        aria-label={statusLabel}
                    />
                </Box>
            </Tooltip>
            {noShow ? (
                <Tooltip title={noShowTitle || 'No-show'}>
                    <Box component="span" sx={{ display: 'inline-flex', lineHeight: 0 }}>
                        <PersonOffOutlinedIcon fontSize="small" color="error" aria-label="No-show" />
                    </Box>
                </Tooltip>
            ) : null}
        </Box>
    )
}

/** Native ButtonGroup children so PDF / Receipt / ⋯ share identical size. */
function OrderActionsCell({
    showPdf,
    orderId,
    reservationCode,
    showReceipt,
    onCopySummary,
    menuOptions,
}: {
    showPdf: boolean
    orderId: number
    reservationCode?: string | null
    showReceipt: boolean
    onCopySummary: () => void
    menuOptions: Array<{ label: string; onClick: () => void; disabled?: boolean }>
}) {
    const [pdfAnchor, setPdfAnchor] = useState<null | HTMLElement>(null)
    const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null)

    const handlePdfSelect = (lang: BoardguardPdfLang) => {
        setPdfAnchor(null)
        void downloadTicketPdf(orderId, reservationCode, lang)
    }

    return (
        <>
            <ButtonGroup size="small" variant="outlined" color="primary">
                {showPdf && (
                    <Button
                        variant="outlined"
                        color="primary"
                        aria-label="Baixar bilhete (PDF)"
                        title="Baixar bilhete (PDF)"
                        onClick={(event) => {
                            event.stopPropagation()
                            setPdfAnchor(event.currentTarget)
                        }}
                    >
                        <PictureAsPdfIcon fontSize="small" />
                    </Button>
                )}
                {showReceipt && (
                    <Button variant="outlined" color="primary" onClick={onCopySummary} aria-label="Copiar resumo">
                        <Receipt fontSize="small" />
                    </Button>
                )}
                {menuOptions.length > 0 && (
                    <Button
                        variant="outlined"
                        color="primary"
                        aria-label="Mais ações"
                        onClick={(event) => {
                            event.stopPropagation()
                            setMoreAnchor(event.currentTarget)
                        }}
                    >
                        <MoreHorizIcon fontSize="small" />
                    </Button>
                )}
            </ButtonGroup>
            <MuiMenu
                anchorEl={pdfAnchor}
                open={Boolean(pdfAnchor)}
                onClose={() => setPdfAnchor(null)}
            >
                {BOARDGUARD_PDF_LANG_OPTIONS.map((option) => (
                    <MenuItem key={option.value} onClick={() => handlePdfSelect(option.value)}>
                        {option.label}
                    </MenuItem>
                ))}
            </MuiMenu>
            <MuiMenu
                anchorEl={moreAnchor}
                open={Boolean(moreAnchor)}
                onClose={() => setMoreAnchor(null)}
            >
                {menuOptions.map((option) => (
                    <MenuItem
                        key={option.label}
                        disabled={option.disabled}
                        onClick={() => {
                            setMoreAnchor(null)
                            option.onClick()
                        }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </MuiMenu>
        </>
    )
}

type RowOverride = NoShowFields & { boardguard_ticket?: BoardguardTicket | null };

type Program = {
    company: string;
    active: boolean;
    show_in_bids_filter: boolean;
};

const DEFAULT_PAGINATION = { page: 0, pageSize: 100 };
const DEFAULT_SORT: GridSortModel = [{ field: 'created_at', sort: 'asc' }];
const DEFAULT_FILTER: GridFilterModel = {
    items: [
        {
            field: 'payment.status',
            operator: 'is',
            value: 'AWAITING_ORDER_CONFIRMATION',
        },
    ],
};

const PAYMENT_STATUS_OPTIONS = [
    { value: 'AWAITING_ORDER_CONFIRMATION', label: 'Aguardando Confirmação' },
    { value: 'PENDING', label: 'Pendente' },
    { value: 'PAID', label: 'Pago' },
    { value: 'LATE', label: 'Atrasado' },
    { value: 'CANCELLED', label: 'Cancelado' },
];

const CONFIRMATION_OPTIONS = [
    { value: 'PENDING', label: 'Pendente' },
    { value: 'CONFIRMED', label: 'Confirmada' },
    { value: 'CANCELLED', label: 'Cancelada' },
];

export function Orders () {
    const {me} = useContext(AuthContext)
    const navigate = useNavigate()

    const [accountModalVisibility, accountModalVisibilityControls] = useToggle(false)
    const [bidToViewAccount, setBidToViewAccount] = useState<AccountModalBidsProps['bid']>()
    const [programs] = useAxios(`/program`)
    const [api, callAPI] = useAxios({}, {manual: true})
    const [confirmModalVisible, setConfirmModalVisibility] = useToggle(false)
    const confirmModalAction = useRef<null | (() => Promise<void>)>(null)
    const confirmModalActionLabel = useRef<string>('')

    const [rowSelectionModel, setRowSelectionModel] = useState<Array<string | number>>([]);

    const {
        paginationModel,
        sortModel,
        filterModel,
        setPaginationModel,
        setSortModel,
        setFilterModel,
        apiQueryParams,
        searchParams,
        updateParams,
    } = useGridUrlState({
        defaultPaginationModel: DEFAULT_PAGINATION,
        defaultSortModel: DEFAULT_SORT,
        defaultFilterModel: DEFAULT_FILTER,
    });

    const selectedCompanyValues = useMemo<string[]>(() => {
        const raw = searchParams.get('companies');
        if (!raw) return [];
        return raw.split(',').map((c) => c.trim()).filter(Boolean);
    }, [searchParams]);

    const setSelectedCompanyValues = useCallback((values: string[]) => {
        updateParams((next) => {
            if (!values.length) next.delete('companies');
            else next.set('companies', values.join(','));
            next.delete('page');
        });
    }, [updateParams]);

    const companiesSortedAlphabetically = useMemo(() => {
        const programsData = ((programs?.data as Program[] | undefined) ?? []);
        const allowedCompanies = new Set(
            programsData
                .filter((p) => p.show_in_bids_filter)
                .map((p) => p.company)
        );

        return [...COMPANY_OPTIONS]
            .filter((opt) => allowedCompanies.has(opt.value))
            .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
    }, [programs?.data]);

    const ordersUrl = useMemo(() => {
        const params = new URLSearchParams(apiQueryParams);
        if (selectedCompanyValues.length) {
            params.set('companies', selectedCompanyValues.join(','));
        }
        return `/order?${params.toString()}`;
    }, [apiQueryParams, selectedCompanyValues]);

    const [orders, refetchOrders] = useAxios(ordersUrl, { autoCancel: true });
    const [rowOverrides, setRowOverrides] = useState<Record<number, RowOverride>>({});

    const overrideRow = useCallback((orderId: number, patch: RowOverride) => {
        setRowOverrides((prev) => ({ ...prev, [orderId]: { ...prev[orderId], ...patch } }));
    }, []);

    useEffect(() => {
        setRowOverrides({});
    }, [orders?.data]);

    useOrderBoardguardUpdates((event) => {
        overrideRow(event.order_id, { boardguard_ticket: event.boardguard_ticket, ...pickNoShowFields(event) });
    });

    const tableRows = useMemo(() => {
        const rows: any[] = orders?.data?.rows || [];
        return rows.map((item) => ({
            ...item,
            ...rowOverrides[item.id],
            holder_name: item.account?.holder_name ?? '',
            supplier_name: me?.role !== 'AGENCY' ? item.bid?.user?.name ?? '' : '',
        }));
    }, [orders?.data, me?.role, rowOverrides]);

    const rowCount = orders?.data?.total ?? 0;

    return (
        <Layout>
            <Grid container justifyContent="center" alignItems="flex-start" padding={1} spacing={2}>
                {programs.loading && !programs.data ? (
                    <div style={{
                        display: 'flex',
                        flex: '1',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <Loader />
                    </div>
                ) : (
                    <Fragment>
                    <Grid item xs={12} sm={12}>
                        <Alert color="info" icon={false}>
                            Esta listagem inicialmente mostra apenas emissões com pagamento <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Aguardando Confirmação</span>.<br/>Para ver todos os registros, limpe os filtros da tabela.
                        </Alert>
                    </Grid>
                    {me?.role === 'SELLER' &&  (<Grid item xs={12} sm={12} paddingBottom={2}>
                        <Accordion>
                            <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            >
                            <Typography fontWeight="bold">Tabela de Preços</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                            <Table 
                                columns={[
                                    { field: 'id', headerName: 'Programa', valueGetter: ({row}) => COMPANY_OPTIONS.find(item => item.value === row.company)?.label, flex: 1 },
                                    { field: 'sell_price', headerName: 'Valor de Venda (R$)', flex: 1, valueFormatter: ({value}) => formatMoney(value) },
                                ]}
                                getRowId={({company}) => company}
                                hideFooterPagination
                                rows={programs.data || []}
                                hideFooter
                                slots={undefined}
                            />
                            </AccordionDetails>
                        </Accordion>
                    </Grid>)}
                    {rowSelectionModel.length ? (<Grid item xs={12} sm={12} textAlign="left">
                        <Button
                            onClick={() => {
                                confirmModalAction.current = async () => {
                                    await callAPI({
                                        url: `/order/batch-confirm`,
                                        method: 'PUT',
                                        data: {
                                            ids: rowSelectionModel
                                        }
                                    })

                                    refetchOrders()
                                    setConfirmModalVisibility.setFalse()
                                }

                                    confirmModalActionLabel.current = 'confirmar'
                                setConfirmModalVisibility.setTrue()
                            }}
                            disabled={api.loading}
                        >
                            Confirmar
                        </Button>
                    </Grid>) : null}
                <Grid item xs={12} sm={12}>
                    {companiesSortedAlphabetically.length > 0 && (
                        <Box sx={{ mb: 3, width: 1 }}>
                            <Box
                                sx={{
                                    overflowX: "auto",
                                    pb: 0.5,
                                    "& .MuiToggleButtonGroup-root": { flexWrap: "nowrap" },
                                }}
                            >
                                <ToggleButtonGroup
                                    value={selectedCompanyValues}
                                    onChange={(_, next) => setSelectedCompanyValues(next ?? [])}
                                    aria-label="Filtrar por programa"
                                    size="small"
                                >
                                    {companiesSortedAlphabetically.map((opt) => (
                                        <ToggleButton
                                            key={opt.value}
                                            value={opt.value}
                                            sx={{ whiteSpace: "nowrap", textTransform: "none" }}
                                        >
                                            {opt.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    )}
                    <Paper elevation={1} sx={{ width: '100%', maxWidth: '100%' }}>
                    <Table 
                        rowSelection={true}
                        checkboxSelection
                        keepNonExistentRowsSelected
                        disableRowSelectionOnClick
                        onRowSelectionModelChange={(newRowSelectionModel) => {
                            setRowSelectionModel(newRowSelectionModel);
                        }}
                        rowSelectionModel={rowSelectionModel}
                        isRowSelectable={({row}) => (row.confirmation === 'CANCELLED' || row.confirmation === 'PENDING') && row.payment.status !== 'PAID'}
                        loading={orders.loading || api.loading}
                        paginationMode="server"
                        sortingMode="server"
                        filterMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        sortModel={sortModel}
                        onSortModelChange={setSortModel}
                        filterModel={filterModel}
                        onFilterModelChange={setFilterModel}
                        rowCount={rowCount}
                        pageSizeOptions={[10, 25, 50, 100]}
                        density="standard"
                        getRowHeight={() => 'auto'}
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            '& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox': {
                                minWidth: '40px !important',
                                maxWidth: '40px !important',
                                width: '40px !important',
                            },
                            '& .MuiDataGrid-cell': {
                                py: 1,
                                alignItems: 'flex-start',
                            },
                            '& .MuiDataGrid-cell[data-field="actions"]': {
                                overflow: 'visible',
                            },
                            '& .MuiDataGrid-columnHeader': {
                                whiteSpace: 'normal',
                                lineHeight: 1.2,
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                whiteSpace: 'normal',
                                lineHeight: 1.2,
                            },
                        }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 400 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    customer_name: false,
                                    origin_airport_code: false,
                                    holder_name: false,
                                    airline: false,
                                    pax: false,
                                    miles: false,
                                    sold_k: false,
                                    confirmation: false,
                                    no_show: false,
                                    actions: me?.role === 'ADMIN' || me?.role === 'SELLER' || me?.role === 'AGENCY',
                                    supplier_name: me?.role !== 'AGENCY',
                                    fees: false,
                                    additional_cost: false,
                                    price_per_k: false,
                                    total_profit: false,
                                    description: false,
                                    cancelled_at: false,
                                }
                            }
                        }}
                        columns={[
                            { field: 'id', headerName: 'ID', width: 52, minWidth: 52, type: 'string' },
                            {
                                field: 'created_at',
                                headerName: 'Data',
                                width: 96,
                                minWidth: 96,
                                type: 'date',
                                valueGetter: ({ value }) => new Date(value),
                                valueFormatter: ({ value }) => formatDate(value),
                            },
                            {
                                field: 'flight_date',
                                type: 'date',
                                headerName: 'Voo',
                                valueGetter: ({ row }) => new Date(row.flight_date),
                                valueFormatter: ({ value }) => formatDate(value),
                                flex: 1.4,
                                minWidth: 120,
                                renderCell: ({ row, formattedValue }) => {
                                    const airlineOption = AIRLINES_OPTIONS.find((item) => item.value === row.airline)
                                    const airlineCode = row.airline || '—'
                                    const airlineLabel = airlineOption?.label || airlineCode
                                    const origin = row.origin_airport_code || '—'
                                    const pax = row.order_pax?.[0]
                                    const paxLabel = pax
                                        ? [pax.name, pax.surname].filter(Boolean).join(' / ')
                                        : row.pax_name || ''
                                    return (
                                        <StackCell
                                            primary={formattedValue}
                                            secondary={
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15 }}>
                                                    <span title={airlineLabel}>{`${airlineCode} · ${origin}`}</span>
                                                    {paxLabel ? <span>{paxLabel}</span> : null}
                                                </Box>
                                            }
                                        />
                                    )
                                },
                            },
                            { field: 'origin_airport_code', headerName: 'Origem', width: 90 },
                            {
                                field: 'author_name',
                                headerName: 'Emissor / Cliente',
                                flex: 1.2,
                                minWidth: 110,
                                // Combined value so filtering matches the secondary "Cliente" line;
                                // server filter also ORs author_name + customer.name.
                                valueGetter: ({ row }) =>
                                    [row.author_name, row.customer?.name].filter(Boolean).join(' ').trim(),
                                renderCell: ({ row }) => (
                                    <StackCell
                                        primary={row.author_name || '—'}
                                        secondary={row.customer?.name || '—'}
                                    />
                                ),
                            },
                            { field: 'customer_name', headerName: 'Cliente', flex: 1, valueGetter: (params) => params.row?.customer?.name || '' },
                            {
                                field: 'reservation_code',
                                headerName: 'Localizador',
                                flex: 0.7,
                                minWidth: 88,
                                valueGetter: ({ row }) => {
                                    const loc1 = row.reservation_code || ''
                                    const loc2 = row.reservation_code_2 || ''
                                    return loc2 ? `${loc1}/${loc2}` : loc1
                                },
                                renderCell: ({ row }) => (
                                    <StackCell
                                        primary={row.reservation_code || '—'}
                                        secondary={row.reservation_code_2 || undefined}
                                    />
                                ),
                            },
                            {
                                field: 'supplier_name',
                                headerName: 'Fornecedor / Titular',
                                flex: 1.2,
                                minWidth: 110,
                                // Combined value so filtering matches the secondary "Titular" line;
                                // server filter also ORs bid.user.name + account.holder_name.
                                valueGetter: ({ row }) =>
                                    [row.supplier_name, row.holder_name].filter(Boolean).join(' ').trim(),
                                renderCell: ({ row }) => (
                                    <StackCell
                                        primary={row.supplier_name || '—'}
                                        secondary={row.holder_name || '—'}
                                    />
                                ),
                            },
                            { field: 'holder_name', headerName: 'Titular', flex: 1 },
                            { field: 'airline', headerName: 'Companhia', flex: 1 },
                            { field: 'pax', headerName: 'Qtd. Pax', type: 'number', width: 40 },
                            {
                                field: 'company',
                                headerName: 'Programa',
                                flex: 1,
                                minWidth: 110,
                                type: 'singleSelect',
                                valueOptions: COMPANY_OPTIONS_SORTED.map((c) => ({ value: c.value, label: c.label })),
                                valueGetter: ({row}) => row.bid?.company,
                                valueFormatter: ({value}) => COMPANY_OPTIONS.find(item => item.value === value)?.label,
                                renderCell: ({ formattedValue, row }) => (
                                    <StackCell
                                        primary={formattedValue || '—'}
                                        secondary={`${Number(row.miles).toLocaleString('pt-BR')} · ${row.pax ?? 0} pax`}
                                    />
                                ),
                            },
                            { field: 'miles', headerName: 'Qtd.', width: 80, type: 'number', valueFormatter: ({value }) => {
                                return Number(value).toLocaleString('pt-BR')
                            }},
                            { field: 'fees', headerName: 'Taxas', width: 80, type: 'number', valueFormatter: ({ value }) => {
                                return formatMoney(value)
                            }},
                            { field: 'additional_cost', headerName: 'Custos Adicionais', width: 80, type: 'number', valueFormatter: ({value }) => {
                                return formatMoney(value)
                            }},
                            { field: 'price_per_k', headerName: 'Custo do Milheiro', width: 80, type: 'number', valueGetter: data => data.row.bid?.price_per_k, valueFormatter: ({value }) => {
                                return formatMoney(value)
                            }},
                            {
                                field: 'sold_k',
                                headerName: 'Mil. Vendido',
                                width: 100,
                                sortable: false,
                                filterable: false,
                                valueGetter: ({row}) => {
                                    const totalMilesCost = currency(row.sell_price).subtract(row.fees).subtract(row.additional_cost).value
                                    return currency(totalMilesCost).divide(currency(row.miles, {precision: 3}).divide(1000)).value
                                },
                                valueFormatter: ({ value }) => formatMoney(value),
                            },
                            {
                                field: 'total_profit',
                                headerName: 'RAM',
                                width: 80,
                                sortable: false,
                                filterable: false,
                                valueGetter: data => currency(data.row.sell_price).subtract(data.row.total_cost).value,
                                valueFormatter: ({value }) => formatMoney(value),
                            },
                            { field: 'description', headerName: 'Observações', width: 100, },
                            {
                                field: 'sell_price',
                                headerName: 'Venda / RAM',
                                flex: 0.9,
                                minWidth: 108,
                                type: 'number',
                                valueGetter: data => Number(data.row.sell_price),
                                valueFormatter: ({ value }) => formatMoney(value),
                                renderCell: ({ row, formattedValue }) => {
                                    const soldK = currency(row.sell_price).subtract(row.fees).subtract(row.additional_cost)
                                        .divide(currency(row.miles, { precision: 3 }).divide(1000)).value
                                    const ram = currency(row.sell_price).subtract(row.total_cost).value
                                    return (
                                        <StackCell
                                            primary={formattedValue}
                                            secondary={`${formatMoney(soldK)}/k · RAM ${formatMoney(ram)}`}
                                        />
                                    )
                                },
                            },
                            {
                                field: 'payment.status',
                                headerName: 'Status',
                                flex: 0.75,
                                minWidth: 96,
                                type: 'singleSelect',
                                valueOptions: PAYMENT_STATUS_OPTIONS,
                                valueGetter: ({row}) => row.payment?.status,
                                renderCell: ({ value, row }) => (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, py: 0.25 }}>
                                        <Chip
                                            size="small"
                                            color={getPaymentStatusColor(value) || 'default'}
                                            label={getCompactPaymentStatusLabel(value)}
                                            title={getPaymentStatusLabel(value) || undefined}
                                            sx={statusChipSx}
                                        />
                                        <Chip
                                            size="small"
                                            color={getOrderConfirmationColor(row) || 'default'}
                                            label={getOrderConfirmationLabel(row.confirmation) || row.confirmation || '—'}
                                            sx={statusChipSx}
                                        />
                                    </Box>
                                ),
                            },
                            {
                                field: 'confirmation',
                                headerName: 'Status (48h)',
                                width: 110,
                                type: 'singleSelect',
                                valueOptions: CONFIRMATION_OPTIONS,
                                renderCell: ({ value, row }) => (
                                    <Chip
                                        size="small"
                                        color={getOrderConfirmationColor(row) || 'default'}
                                        label={getOrderConfirmationLabel(value) || value || '—'}
                                        sx={statusChipSx}
                                    />
                                ),
                            },
                            {
                                field: 'boardguard_ticket.status',
                                headerName: 'Mon.',
                                width: 52,
                                minWidth: 52,
                                type: 'singleSelect',
                                valueOptions: BOARDGUARD_STATUS_OPTIONS,
                                valueGetter: ({ row }) => getBoardguardDisplayStatus(row.boardguard_ticket),
                                renderCell: ({ value, row }) => (
                                    <MonitorIcons
                                        status={value}
                                        ticket={row.boardguard_ticket}
                                        noShow={Boolean(row.no_show)}
                                        noShowTitle={getNoShowTooltip(row, row.boardguard_ticket)}
                                    />
                                ),
                            },
                            {
                                field: 'no_show',
                                headerName: 'No-show',
                                type: 'boolean',
                                width: 80,
                                renderCell: ({ row }) => row.no_show ? (
                                    <Tooltip title={getNoShowTooltip(row, row.boardguard_ticket)}>
                                        <PersonOffOutlinedIcon fontSize="small" color="error" aria-label="No-show" />
                                    </Tooltip>
                                ) : null,
                            },
                            {
                                field: 'cancelled_at',
                                headerName: 'Data de Cancelamento',
                                type: 'date',
                                width: 140,
                                valueGetter: ({value}) => value ? new Date(value) : null,
                                valueFormatter: ({value}) => value ? formatDateTime(value) : '',
                            },
                            {
                                field: 'actions',
                                headerName: '',
                                sortable: false,
                                filterable: false,
                                width: 128,
                                minWidth: 128,
                                maxWidth: 128,
                                resizable: false,
                                disableColumnMenu: true,
                                renderCell: ({row}) => {
                                    const menuOptions: Array<{
                                        label: string,
                                        onClick: () => void,
                                        disabled?: boolean
                                    }> = [
                                        {
                                            label: 'Gerar Resumo',
                                            onClick: () => {
                                                const text = composeSummary({
                                                    id: row.id,
                                                    company: COMPANY_OPTIONS.find(item => item.value === row.bid.company)?.label || '',
                                                    fees: Number(row.fees),
                                                    miles: Number(row.miles),
                                                    reservationCode: row.reservation_code,
                                                    total: Number(row.sell_price),
                                                    additional_cost: Number(row.additional_cost || 0)
                                                })
                                                
                                                navigator.clipboard.writeText(text)

                                                enqueueSnackbar('Resumo copiado!', {variant: 'success'})
                                            }
                                        },
                                        {
                                            label: 'Gerar Pix Copia e Cola',
                                            onClick: async () => {
                                                const qrCodePix = await QrCodePix({
                                                    version: '01',
                                                    key: '45176365000100',
                                                    name: 'PAX VIAGENS',
                                                    city: 'Cuiabá',
                                                    transactionId: `${row.id}`,
                                                    message: `Emissão: ${row.id}`,
                                                    cep: '78060684',
                                                    value: currency(row.sell_price, {precision: 2}).value,
                                                })

                                                navigator.clipboard.writeText(qrCodePix.payload())

                                                enqueueSnackbar('PIX Copia e Cola copiado!', {variant: 'success'})
                                            }
                                        },
                                        { 
                                            label: 'Editar', 
                                            onClick: async () => {
                                                navigate(`/admin/emissoes/${row.id}`)
                                            }
                                        },
                                        { 
                                            label: 'Ver Conta', 
                                            onClick: async () => {
                                                setBidToViewAccount(row.bid)
                                                accountModalVisibilityControls.setTrue()
                                            }
                                        },
                                    ]

                                    if ((row.confirmation === 'CONFIRMED' || row.confirmation === 'PENDING') && row.payment.status !== 'PAID') {
                                        menuOptions.push({ 
                                            label: 'Cancelar', 
                                            onClick: async () => {
                                                confirmModalAction.current = async () => {
                                                    await callAPI({
                                                        url: `/order/${row.id}/cancel`,
                                                        method: 'POST'
                                                    })
                                                    refetchOrders()
                                                    setConfirmModalVisibility.setFalse()
                                                }

                                                    confirmModalActionLabel.current = 'cancelar'
                                                setConfirmModalVisibility.setTrue()
                                            }
                                        })
                                    }

                                    if ((row.confirmation === 'CANCELLED' || row.confirmation === 'PENDING') && row.payment.status !== 'PAID') {
                                        menuOptions.unshift({ 
                                            label: 'Confirmar', 
                                            onClick: async () => {
                                                confirmModalAction.current = async () => {
                                                    await callAPI({
                                                        url: `/order/${row.id}/confirm`,
                                                        method: 'POST'
                                                    })

                                                    refetchOrders()
                                                    setConfirmModalVisibility.setFalse()
                                                }

                                                confirmModalActionLabel.current = 'confirmar'
                                                setConfirmModalVisibility.setTrue()
                                            }
                                        })
                                    }

                                    const ticket: BoardguardTicket | null | undefined = row.boardguard_ticket

                                    if (!ticket && row.boardguard_supported) {
                                        menuOptions.push({
                                            label: BOARDGUARD_MENU_LABELS.monitor,
                                            onClick: async () => {
                                                const created = await monitorOrderOnBoardguard(row.id)
                                                if (created) {
                                                    overrideRow(row.id, { boardguard_ticket: created })
                                                }
                                            }
                                        })
                                    }

                                    if (ticket?.pdf_ready) {
                                        menuOptions.push(
                                            {
                                                label: BOARDGUARD_MENU_LABELS.pdfPt,
                                                onClick: () => downloadTicketPdf(row.id, row.reservation_code, 'pt'),
                                            },
                                            {
                                                label: BOARDGUARD_MENU_LABELS.pdfEn,
                                                onClick: () => downloadTicketPdf(row.id, row.reservation_code, 'en'),
                                            },
                                        )
                                    }

                                    if (ticket && !ticket.archived) {
                                        menuOptions.push({
                                            label: BOARDGUARD_MENU_LABELS.check,
                                            onClick: () => triggerBoardguardCheck(row.id)
                                        })
                                    }

                                    menuOptions.push({
                                        label: row.no_show ? NO_SHOW_MENU_LABELS[1] : NO_SHOW_MENU_LABELS[0],
                                        onClick: async () => {
                                            const updated = await setOrderNoShow(row.id, !row.no_show)
                                            if (updated) overrideRow(row.id, pickNoShowFields(updated))
                                        }
                                    })

                                    const boardguardLabels: string[] = Object.values(BOARDGUARD_MENU_LABELS)
                                    const visibleMenuOptions = me?.role === 'AGENCY'
                                        ? menuOptions.filter(option => boardguardLabels.includes(option.label))
                                        : me?.role === 'SELLER'
                                            ? menuOptions.filter(option => ['Gerar Pix Copia e Cola', 'Gerar Resumo', 'Editar', ...boardguardLabels, ...NO_SHOW_MENU_LABELS].includes(option.label))
                                            : menuOptions

                                    return (
                                        <OrderActionsCell
                                            showPdf={Boolean(ticket?.pdf_ready)}
                                            orderId={row.id}
                                            reservationCode={row.reservation_code}
                                            showReceipt={me?.role !== 'AGENCY'}
                                            menuOptions={visibleMenuOptions}
                                            onCopySummary={() => {
                                                const summary = composeSummary({
                                                    id: row.id,
                                                    reservationCode: row.reservation_code,
                                                    fees: Number(row.fees),
                                                    miles: Number(row.miles),
                                                    total: Number(row.sell_price),
                                                    additional_cost: Number(row.additional_cost || 0),
                                                    company: COMPANY_OPTIONS.find(item => item.value === row.bid.company)?.label || ''
                                                })
                                                navigator.clipboard.writeText(summary)
                                                enqueueSnackbar('Resumo copiado para a área de transferência.', {variant: 'success'})
                                            }}
                                        />
                                    )
                                }
                            }
                        ]}
                        rows={tableRows}
                    />
                    </Paper>
                </Grid>
                </Fragment>)}
            </Grid>
            {bidToViewAccount && (<AccountModalBids 
                accountModalVisibility={accountModalVisibility} 
                accountModalVisibilityControls={accountModalVisibilityControls}
                bid={bidToViewAccount}
                loading={false}
                onAccountSaved={refetchOrders}
            />)}
            {confirmModalAction.current && confirmModalVisible && (
            <Modal
                title="Atenção!"
                open={confirmModalVisible}
                onConfirm={confirmModalAction.current}
                confirmLabel="Confirmar"
                onClose={() => {
                    setConfirmModalVisibility.setFalse()
                    confirmModalAction.current = null
                }}
            >
                <div>
                    Esta ação é irreversível e não pode ser desfeita.<br/>
                    Você tem certeza que deseja <span style={{fontWeight: 'bold'}}>{confirmModalActionLabel.current.toLocaleUpperCase()}</span> esta(s) emissão(ões)?
                </div>
            </Modal>)}
        </Layout>
    )
}
